import { EntityRegistry } from '../../core/EntityRegistry';
import { EntityState, ZoneTileType, InventoryItem, EntityRole } from '../../core/Types';
import { EventBus } from '../../core/EventBus';
import { IsoRenderer } from '../view/IsoRenderer';
import {
  TILE_PROPERTY_SPECS,
  updateTilePropertySpec,
  resetTilePropertySpecs,
  getAllTileTypes,
  TilePropertySpec,
} from '../world/TilePropertySystem';
import { addItemToInventory, initEntityInventory20 } from '../character/CharacterSystem';
import { getCemeteryMonsterConfigs } from '../world/DungeonMap';
import { Currency } from '../../core/Currency';

export interface AdminCheatsState {
  godMode: boolean;
  zeroCooldown: boolean;
  superSpeedMult: number;
  instaKill: boolean;
}

export interface GeneratedPropPreset {
  id: string;
  type: string;
  name: string;
  icon: string;
  desc: string;
  badge: string;
  draw: (ctx: CanvasRenderingContext2D, sx: number, sy: number, time: number) => void;
}

export class AdminConsoleHUD {
  private registry: EntityRegistry;
  private renderer: IsoRenderer;
  private teleportCallback: (zoneId: number, spawnX?: number, spawnY?: number) => void;

  public isOpen = false;
  public activeTab: 'cheats' | 'terrain' | 'teleport' | 'items' | 'spawner' | 'forge' = 'cheats';

  // Cheats State
  public cheats: AdminCheatsState = {
    godMode: false,
    zeroCooldown: false,
    superSpeedMult: 1.0,
    instaKill: false,
  };

  // Terrain / Tile Brush State
  public brushActive = false;
  public brushTile: ZoneTileType = 'cobblestone';
  public brushSize: 1 | 3 | 5 = 1;
  public selectedPropTile: ZoneTileType = 'cobblestone';

  // Object Placement, Demolition & Move State
  public placementActive = false;
  public pendingProp: { type: string; name: string; customDraw?: any } | null = null;
  public demolitionActive = false;
  public moveActive = false;
  public movingTarget: {
    kind: 'prop' | 'entity';
    id: string;
    name: string;
    propRef?: any;
    entityRef?: EntityState;
    originalX: number;
    originalY: number;
  } | null = null;

  // AI Forge Presets
  public forgePresets: GeneratedPropPreset[] = [];
  public selectedPresetIndex = 0;

  // UI DOM Container
  private containerEl: HTMLElement | null = null;

  constructor(
    registry: EntityRegistry,
    renderer: IsoRenderer,
    teleportCallback: (zoneId: number, spawnX?: number, spawnY?: number) => void
  ) {
    this.registry = registry;
    this.renderer = renderer;
    this.teleportCallback = teleportCallback;

    this.initDefaultForgePresets();
    this.buildDOM();
    this.bindGlobalKeys();

    // Expose GM object on window for developer testing
    (window as any).TerraAnimaGM = this;
    (window as any).GM = this;
  }

  // ==========================================================================
  // 1. GAME LOOP HOOKS (GodMode, Zero Cooldown, Super Speed, Insta Kill)
  // ==========================================================================
  public update(dt: number): void {
    const player = this.registry.getPlayer();
    if (!player) return;

    // A. God Mode: Lock HP/SP/MP at maximum and disable death
    if (this.cheats.godMode) {
      player.resources.hp = player.resources.maxHp;
      player.resources.sp = player.resources.maxSp;
      player.resources.mp = player.resources.maxMp;
      player.resources.conc = player.resources.maxConc;
      player.isDead = false;
    }

    // B. Zero Cooldown: Instantly flush all cooldown timers
    if (this.cheats.zeroCooldown) {
      player.cooldowns = {};
    }

    // C. Super Speed Multiplier
    if (this.cheats.superSpeedMult !== 1.0) {
      const baseSpeed = 4.0;
      player.speed = baseSpeed * this.cheats.superSpeedMult;
    }
  }

  // ==========================================================================
  // 2. CANVAS INTERACTION HOOKS (Tile Brush Painting & Object Placement)
  // ==========================================================================
  public handleCanvasClick(worldX: number, worldY: number): boolean {
    const rx = Math.round(worldX);
    const ry = Math.round(worldY);
    const zone = this.renderer.getActiveZoneData();

    // 1) Demolition Mode: Click any prop or NPC/Monster to delete/despawn it!
    if (this.demolitionActive) {
      // First check entities (NPCs, Monsters, Resources)
      const allEnts = this.registry.getAll();
      const entIdx = allEnts.findIndex((e) => e.category !== 'player' && Math.hypot(e.x - rx, e.y - ry) <= 1.4);
      if (entIdx !== -1) {
        const ent = allEnts[entIdx];
        this.registry.unregister(ent.id);
        const player = this.registry.getPlayer();
        if (player && player.targetId === ent.id) player.targetId = null;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 엔티티 '${ent.name}' (${Math.round(ent.x)}, ${Math.round(ent.y)}) 영구 철거/삭제 완료!`,
          category: 'system',
        });
        return true;
      }

      // Then check zone props
      const idx = zone.props.findIndex((p) => Math.hypot(p.x - rx, p.y - ry) <= 1.4);
      if (idx !== -1) {
        const removed = zone.props.splice(idx, 1)[0];
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 오브젝트 '${removed.name}' (${removed.x}, ${removed.y}) 철거 완료!`,
          category: 'system',
        });
        return true;
      }
      return false;
    }

    // 1b) Move Mode: Select object/NPC and relocate to clicked tile
    if (this.moveActive) {
      // Step A: If no target selected yet, pick up prop or NPC/Monster near click
      if (!this.movingTarget) {
        // Priority 1: Entity (NPC, Monster, Resource)
        const allEnts = this.registry.getAll();
        const ent = allEnts.find((e) => e.category !== 'player' && Math.hypot(e.x - rx, e.y - ry) <= 1.5);
        if (ent) {
          this.movingTarget = {
            kind: 'entity',
            id: ent.id,
            name: ent.name,
            entityRef: ent,
            originalX: ent.x,
            originalY: ent.y,
          };
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `[👑 GM] 이동 대상 선택: '${ent.name}' (${Math.round(ent.x)}, ${Math.round(ent.y)}) ➔ 옮겨놓을 바닥 타일을 클릭하세요!`,
            category: 'system',
          });
          this.render();
          return true;
        }

        // Priority 2: Zone Prop (buildings, decorations, trees, etc.)
        const p = zone.props.find((pr) => Math.hypot(pr.x - rx, pr.y - ry) <= 1.5);
        if (p) {
          this.movingTarget = {
            kind: 'prop',
            id: p.id,
            name: p.name,
            propRef: p,
            originalX: p.x,
            originalY: p.y,
          };
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `[👑 GM] 이동 대상 선택: '${p.name}' (${p.x}, ${p.y}) ➔ 옮겨놓을 바닥 타일을 클릭하세요!`,
            category: 'system',
          });
          this.render();
          return true;
        }

        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 이동할 대상(NPC 또는 오브젝트)을 찾지 못했습니다. 대상 근처를 클릭하세요.`,
          category: 'system',
        });
        return false;
      }

      // Step B: Already have a target selected -> Move it to (rx, ry)!
      if (rx >= 0 && rx < zone.width && ry >= 0 && ry < zone.height) {
        if (this.movingTarget.kind === 'prop' && this.movingTarget.propRef) {
          this.movingTarget.propRef.x = rx;
          this.movingTarget.propRef.y = ry;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `[👑 GM] 오브젝트 '${this.movingTarget.name}' 이동 완료: (${this.movingTarget.originalX}, ${this.movingTarget.originalY}) ➔ (${rx}, ${ry})`,
            category: 'system',
          });
        } else if (this.movingTarget.kind === 'entity' && this.movingTarget.entityRef) {
          this.movingTarget.entityRef.x = rx;
          this.movingTarget.entityRef.y = ry;
          this.movingTarget.entityRef.vx = 0;
          this.movingTarget.entityRef.vy = 0;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `[👑 GM] 엔티티 '${this.movingTarget.name}' 이동 완료: (${Math.round(this.movingTarget.originalX)}, ${Math.round(this.movingTarget.originalY)}) ➔ (${rx}, ${ry})`,
            category: 'system',
          });
        }

        // Reset moving target after relocating
        this.movingTarget = null;
        this.render();
        return true;
      }
      return false;
    }

    // 2) Object Placement Mode: Place pending prop
    if (this.placementActive && this.pendingProp) {
      if (rx >= 0 && rx < zone.width && ry >= 0 && ry < zone.height) {
        const newId = `custom_placed_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        const placed = {
          id: newId,
          type: this.pendingProp.type,
          name: this.pendingProp.name,
          x: rx,
          y: ry,
          customDraw: this.pendingProp.customDraw,
        };
        zone.props.push(placed);
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 필드에 '${placed.name}' (${rx}, ${ry}) 설치 완료!`,
          category: 'system',
        });
        // Turn off placement mode after one click unless Shift is held
        this.placementActive = false;
        this.pendingProp = null;
        this.render();
        return true;
      }
    }

    // 3) Tile Brush Painting Mode
    if (this.brushActive) {
      const radius = Math.floor(this.brushSize / 2);
      let changed = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = rx + dx;
          const ny = ry + dy;
          if (nx >= 0 && nx < zone.width && ny >= 0 && ny < zone.height) {
            zone.tiles[ny][nx] = this.brushTile;
            changed++;
          }
        }
      }
      if (changed > 0) {
        this.renderer.rebindGroundCache();
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 지형 타일 페인팅: (${rx}, ${ry}) 반경 ${this.brushSize}x${this.brushSize} -> '${this.brushTile}'`,
          category: 'system',
        });
        return true;
      }
    }

    return false;
  }

  // ==========================================================================
  // 3. UI WINDOW CONTROLS
  // ==========================================================================
  private isPlayerAdmin(): boolean {
    const activePlayer = this.registry.getPlayer();
    if (!activePlayer) return false;
    const job = (activePlayer.job || '').toLowerCase();
    const role = (activePlayer.role || '').toLowerCase();
    return (
      job.includes('관리자') ||
      job.includes('admin') ||
      role === 'admin' ||
      (activePlayer as any).isAdmin === true ||
      activePlayer.appearance?.isKing === true ||
      job.includes('국왕') ||
      job.includes('lord british')
    );
  }

  public toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  public open(): void {
    // 관리자 직업/권한을 부여받지 않은 캐릭터라도 F12를 누르면 즉시 관리자 권한 부여 및 콘솔 활성화
    const activePlayer = this.registry.getPlayer();
    if (activePlayer && !this.isPlayerAdmin()) {
      (activePlayer as any).isAdmin = true;
      activePlayer.role = 'admin';
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 관리자 콘솔(F12) 호출: [${activePlayer.name}] 캐릭터에게 최고 관리자(Admin) 권한이 자동으로 부여되었습니다.`,
        category: 'system',
      });
    }

    this.isOpen = true;
    if (this.containerEl) {
      this.containerEl.style.display = 'block';
      this.render();
    }
  }

  public close(): void {
    this.isOpen = false;
    this.brushActive = false;
    this.placementActive = false;
    this.demolitionActive = false;
    this.moveActive = false;
    this.movingTarget = null;
    if (this.containerEl) {
      this.containerEl.style.display = 'none';
    }
  }

  private bindGlobalKeys(): void {
    window.addEventListener('keydown', (e) => {
      // 월드 상태가 아니면 F12나 GM키 무시
      if ((window as any).__TERRA_GAME_STATE__ && (window as any).__TERRA_GAME_STATE__() !== 'IN_WORLD') {
        return;
      }
      // F12 or Backquote (`) toggles Admin Console
      if (
        e.key === 'F12' ||
        e.code === 'F12' ||
        e.key === 'F10' ||
        (e.key === '`' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName))
      ) {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      }
      // Esc closes panel if open
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  // ==========================================================================
  // 4. DOM CREATION & RENDERING
  // ==========================================================================
  private buildDOM(): void {
    this.containerEl = document.createElement('div');
    this.containerEl.id = 'terra-anima-gm-console';
    this.containerEl.style.cssText = `
      position: fixed;
      top: 60px;
      right: 24px;
      width: 580px;
      max-height: 82vh;
      background: rgba(15, 23, 42, 0.96);
      border: 2px solid #f59e0b;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.7), 0 0 20px rgba(245, 158, 11, 0.35);
      border-radius: 12px;
      color: #e2e8f0;
      font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
      z-index: 99999;
      display: none;
      flex-direction: column;
      overflow: hidden;
      backdrop-filter: blur(12px);
    `;

    document.body.appendChild(this.containerEl);
  }

  public mount(parent: HTMLElement = document.body): void {
    if (this.containerEl && this.containerEl.parentElement !== parent) {
      parent.appendChild(this.containerEl);
    }
  }

  public render(): void {
    if (!this.containerEl || !this.isOpen) return;

    const player = this.registry.getPlayer() || null;
    const zone = this.renderer.getActiveZoneData();
    const target: EntityState | null = (player?.targetId ? this.registry.get(player.targetId) : null) ?? null;

    this.containerEl.innerHTML = `
      <!-- HEADER -->
      <div style="background:linear-gradient(90deg, #1e293b, #0f172a); padding:10px 14px; border-bottom:1.5px solid #f59e0b; display:flex; justify-content:space-between; align-items:center;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="font-size:1.3rem;">👑</span>
          <div>
            <div style="font-weight:900; font-size:0.95rem; color:#fde047; letter-spacing:0.5px;">TERRA ANIMA GM CONSOLE (전능 관리자)</div>
            <div style="font-size:0.7rem; color:#94a3b8;">Zone: ${zone.id}. ${zone.name} | Pos: (${player ? Math.round(player.x) : 0}, ${player ? Math.round(player.y) : 0})</div>
          </div>
        </div>
        <div style="display:flex; gap:6px;">
          <button id="gm-btn-close" style="background:#ef4444; border:none; color:#fff; width:26px; height:26px; border-radius:6px; cursor:pointer; font-weight:bold;">✕</button>
        </div>
      </div>

      <!-- TAB NAVIGATION -->
      <div style="display:flex; background:#0b1329; border-bottom:1px solid #334155; padding:4px 6px; gap:4px; overflow-x:auto;">
        ${this.renderTabBtn('cheats', '⚡ 치트 & 능력')}
        ${this.renderTabBtn('terrain', '🗺️ 지형 & 타일')}
        ${this.renderTabBtn('teleport', '🌀 존 워프')}
        ${this.renderTabBtn('items', '🎒 인벤 & 타겟')}
        ${this.renderTabBtn('spawner', '👾 몹 & NPC')}
        ${this.renderTabBtn('forge', '🔮 AI 제작소')}
      </div>

      <!-- TAB CONTENT BODY -->
      <div style="padding:14px; overflow-y:auto; max-height:calc(82vh - 100px); display:flex; flex-direction:column; gap:12px;">
        ${this.renderActiveTabContent(player, zone, target)}
      </div>
    `;

    this.bindDOMEvents(player, zone, target);
  }

  private renderTabBtn(tab: typeof this.activeTab, label: string): string {
    const isAct = this.activeTab === tab;
    return `
      <button data-gm-tab="${tab}" style="
        background: ${isAct ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#1e293b'};
        color: ${isAct ? '#000' : '#cbd5e1'};
        font-weight: ${isAct ? '900' : 'bold'};
        border: 1px solid ${isAct ? '#fbbf24' : '#475569'};
        padding: 6px 10px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.78rem;
        white-space: nowrap;
      ">${label}</button>
    `;
  }

  private renderActiveTabContent(player: EntityState | null, zone: any, target: EntityState | null): string {
    switch (this.activeTab) {
      case 'cheats':
        return this.renderCheatsTab(player);
      case 'terrain':
        return this.renderTerrainTab(zone);
      case 'teleport':
        return this.renderTeleportTab();
      case 'items':
        return this.renderItemsTab(player, target);
      case 'spawner':
        return this.renderSpawnerTab(player);
      case 'forge':
        return this.renderForgeTab();
    }
  }

  // ==========================================================================
  // TAB 1: ⚡ CHEATS & POWERS
  // ==========================================================================
  private renderCheatsTab(player: EntityState | null): string {
    return `
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="font-weight:bold; font-size:0.85rem; color:#fde047; margin-bottom:10px;">⚡ 관리자 전용 전투 치트</div>
        
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
          <!-- God Mode -->
          <label style="display:flex; align-items:center; gap:8px; background:#0f172a; padding:8px 10px; border-radius:6px; cursor:pointer;">
            <input type="checkbox" id="gm-chk-godmode" ${this.cheats.godMode ? 'checked' : ''} style="width:16px; height:16px; cursor:pointer;">
            <div>
              <div style="font-size:0.82rem; font-weight:bold; color:#38bdf8;">🛡️ 무적 신 모드 (God Mode)</div>
              <div style="font-size:0.7rem; color:#94a3b8;">체력/마나 무한, 피해 면역</div>
            </div>
          </label>

          <!-- Zero Cooldown -->
          <label style="display:flex; align-items:center; gap:8px; background:#0f172a; padding:8px 10px; border-radius:6px; cursor:pointer;">
            <input type="checkbox" id="gm-chk-zerocd" ${this.cheats.zeroCooldown ? 'checked' : ''} style="width:16px; height:16px; cursor:pointer;">
            <div>
              <div style="font-size:0.82rem; font-weight:bold; color:#a855f7;">⚡ 쿨타임 0초 (No Cooldown)</div>
              <div style="font-size:0.7rem; color:#94a3b8;">모든 스킬 즉시 연속 시전</div>
            </div>
          </label>

          <!-- Insta Kill -->
          <label style="display:flex; align-items:center; gap:8px; background:#0f172a; padding:8px 10px; border-radius:6px; cursor:pointer;">
            <input type="checkbox" id="gm-chk-instakill" ${this.cheats.instaKill ? 'checked' : ''} style="width:16px; height:16px; cursor:pointer;">
            <div>
              <div style="font-size:0.82rem; font-weight:bold; color:#ef4444;">💥 원펀치 즉사 (Insta-Kill)</div>
              <div style="font-size:0.7rem; color:#94a3b8;">공격 시 99,999 확정 치명타</div>
            </div>
          </label>

          <!-- Super Speed -->
          <div style="background:#0f172a; padding:8px 10px; border-radius:6px;">
            <div style="font-size:0.82rem; font-weight:bold; color:#4ade80;">🏃 기동 속도 배율</div>
            <select id="gm-sel-speed" style="margin-top:4px; width:100%; background:#1e293b; color:#fff; border:1px solid #475569; padding:4px; border-radius:4px; font-size:0.75rem;">
              <option value="1.0" ${this.cheats.superSpeedMult === 1.0 ? 'selected' : ''}>1.0x (표준 보행)</option>
              <option value="1.5" ${this.cheats.superSpeedMult === 1.5 ? 'selected' : ''}>1.5x (질주)</option>
              <option value="2.0" ${this.cheats.superSpeedMult === 2.0 ? 'selected' : ''}>2.0x (쾌속 기동)</option>
              <option value="3.0" ${this.cheats.superSpeedMult === 3.0 ? 'selected' : ''}>3.0x (초음속 주행)</option>
              <option value="5.0" ${this.cheats.superSpeedMult === 5.0 ? 'selected' : ''}>5.0x (빛의 속도)</option>
            </select>
          </div>
        </div>

        <!-- Party Heal Button -->
        <div style="margin-top:10px; display:flex; gap:8px;">
          <button id="gm-btn-fullheal" style="flex:1; background:linear-gradient(135deg, #10b981, #059669); border:none; color:#fff; font-weight:bold; padding:8px; border-radius:6px; cursor:pointer; font-size:0.8rem;">
            💚 파티 전원 즉시 부활 & HP/MP 100% 완전 회복
          </button>
        </div>

        <!-- King Lord British Avatar Mode (User Request!) -->
        <div style="margin-top:10px; background:#0f172a; border:1.5px solid #f59e0b; padding:10px 12px; border-radius:8px; display:flex; justify-content:space-between; align-items:center; box-shadow:0 0 12px rgba(245, 158, 11, 0.25);">
          <div>
            <div style="font-size:0.84rem; font-weight:900; color:#fde047; display:flex; align-items:center; gap:5px;">
              <span>👑</span> 국왕 (로드 브리티시) 아바타 외형
            </div>
            <div style="font-size:0.7rem; color:#cbd5e1; margin-top:2px;">
              황금 왕관(Crown) + 황금 홀(Scepter) + 에르민 모피 망토 + 왕의 보주(Orb)
            </div>
          </div>
          <button id="gm-btn-toggle-king" style="
            background: ${player?.appearance?.isKing ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #f59e0b, #d97706)'};
            color: #fff; font-weight:900; border:1px solid #fde047; padding:6px 12px; border-radius:6px; cursor:pointer; font-size:0.75rem; white-space:nowrap;
          ">${player?.appearance?.isKing ? '👑 국왕 모드 켜짐' : '👑 국왕 외형 적용'}</button>
        </div>
      </div>

      <!-- Quick Class Hotkey Switcher -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="font-weight:bold; font-size:0.85rem; color:#fde047; margin-bottom:6px;">⚔️ 6대 직업 실시간 즉시 전환</div>
        <div style="font-size:0.72rem; color:#94a3b8; margin-bottom:10px;">클릭 한 번으로 원하는 직업의 7대 스킬과 선봉 캐릭터로 즉시 전환합니다.</div>
        <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:6px;">
          <button data-gm-switch-job="paladin" style="background:#1e3a8a; border:1px solid #3b82f6; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">🛡️ 아서 (성기사)</button>
          <button data-gm-switch-job="wizard" style="background:#4c1d95; border:1px solid #8b5cf6; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">🔮 엘드린 (마법사)</button>
          <button data-gm-switch-job="archer" style="background:#14532d; border:1px solid #22c55e; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">🏹 카엘렌 (명사수)</button>
          <button data-gm-switch-job="cleric" style="background:#854d0e; border:1px solid #eab308; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">✝️ 라이라 (클레릭)</button>
          <button data-gm-switch-job="bard" style="background:#701a75; border:1px solid #d946ef; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">🎵 로빈 (음유시인)</button>
          <button data-gm-switch-job="monk" style="background:#7c2d12; border:1px solid #ea580c; color:#fff; padding:6px; border-radius:6px; font-size:0.75rem; font-weight:bold; cursor:pointer;">🥋 턱 (수도승)</button>
        </div>
      </div>
    `;
  }

  // ==========================================================================
  // TAB 2: 🗺️ TERRAIN & TILE PROPERTIES (USER REQUEST!)
  // ==========================================================================
  private renderTerrainTab(zone: any): string {
    const tileTypes = getAllTileTypes();
    const curSpec = (TILE_PROPERTY_SPECS as any)[this.selectedPropTile] || TILE_PROPERTY_SPECS.grass;

    return `
      <!-- 1. LIVE TILE PAINT BRUSH -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div style="font-weight:bold; font-size:0.85rem; color:#fde047;">🖌️ 실시간 지형 타일 페인팅 브러시</div>
          <button id="gm-btn-toggle-brush" style="
            background: ${this.brushActive ? '#ef4444' : '#10b981'};
            color: #fff; font-weight:bold; border:none; padding:4px 10px; border-radius:6px; cursor:pointer; font-size:0.75rem;
          ">${this.brushActive ? '🔴 브러시 끄기' : '🟢 브러시 켜기'}</button>
        </div>
        <div style="font-size:0.72rem; color:#94a3b8; margin-bottom:10px;">
          브러시를 켜고 필드 캔버스를 클릭/드래그하면 마우스 아래 타일이 실시간으로 교체됩니다.
        </div>

        <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:10px;">
          <div>
            <label style="font-size:0.75rem; color:#cbd5e1; font-weight:bold;">칠할 타일 선택:</label>
            <select id="gm-sel-brush-tile" style="width:100%; margin-top:4px; background:#0f172a; color:#fff; border:1px solid #475569; padding:5px; border-radius:4px; font-size:0.75rem;">
              ${tileTypes.map((t) => `<option value="${t}" ${this.brushTile === t ? 'selected' : ''}>${TILE_PROPERTY_SPECS[t]?.icon || '▫️'} ${TILE_PROPERTY_SPECS[t]?.nameKr || t} (${t})</option>`).join('')}
            </select>
          </div>
          <div>
            <label style="font-size:0.75rem; color:#cbd5e1; font-weight:bold;">브러시 반경 크기:</label>
            <div style="display:flex; gap:6px; margin-top:4px;">
              <button data-brush-size="1" style="flex:1; background:${this.brushSize === 1 ? '#f59e0b' : '#0f172a'}; color:${this.brushSize === 1 ? '#000' : '#fff'}; border:1px solid #475569; padding:4px; border-radius:4px; font-size:0.75rem; font-weight:bold; cursor:pointer;">1x1</button>
              <button data-brush-size="3" style="flex:1; background:${this.brushSize === 3 ? '#f59e0b' : '#0f172a'}; color:${this.brushSize === 3 ? '#000' : '#fff'}; border:1px solid #475569; padding:4px; border-radius:4px; font-size:0.75rem; font-weight:bold; cursor:pointer;">3x3</button>
              <button data-brush-size="5" style="flex:1; background:${this.brushSize === 5 ? '#f59e0b' : '#0f172a'}; color:${this.brushSize === 5 ? '#000' : '#fff'}; border:1px solid #475569; padding:4px; border-radius:4px; font-size:0.75rem; font-weight:bold; cursor:pointer;">5x5</button>
            </div>
          </div>
        </div>

        <button id="gm-btn-paint-underfoot" style="width:100%; background:#334155; border:1px solid #64748b; color:#fde047; font-weight:bold; padding:6px; border-radius:6px; cursor:pointer; font-size:0.75rem;">
          📍 현재 내 캐릭터 발밑 타일을 즉시 '${this.brushTile}'로 변경
        </button>
      </div>

      <!-- 2. LIVE TILE PROPERTY SPEC EDITOR -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div style="font-weight:bold; font-size:0.85rem; color:#38bdf8;">⚙️ 타일 속성값 실시간 편집기 (Tile Property Editor)</div>
          <button id="gm-btn-reset-tile-specs" style="background:#475569; border:none; color:#cbd5e1; padding:3px 8px; border-radius:4px; cursor:pointer; font-size:0.7rem;">기본값 복원</button>
        </div>
        <div style="font-size:0.72rem; color:#94a3b8; margin-bottom:10px;">
          선택한 타일의 물리/속성 규칙을 실시간 수정합니다. (예: 물 위를 걷게 하거나, 벽을 통과하게 만들 수 있습니다!)
        </div>

        <div style="margin-bottom:10px;">
          <label style="font-size:0.75rem; color:#cbd5e1; font-weight:bold;">수정할 타일 규격:</label>
          <select id="gm-sel-spec-tile" style="width:100%; margin-top:4px; background:#0f172a; color:#fde047; border:1px solid #f59e0b; padding:5px; border-radius:4px; font-size:0.8rem; font-weight:bold;">
            ${tileTypes.map((t) => `<option value="${t}" ${this.selectedPropTile === t ? 'selected' : ''}>${TILE_PROPERTY_SPECS[t]?.icon || '▫️'} ${TILE_PROPERTY_SPECS[t]?.nameKr || t} (${t})</option>`).join('')}
          </select>
        </div>

        <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; background:#0f172a; padding:10px; border-radius:6px; margin-bottom:10px;">
          <!-- Walkable Toggle -->
          <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
            <input type="checkbox" id="gm-spec-walkable" ${curSpec.walkable ? 'checked' : ''} style="width:15px; height:15px;">
            <span style="font-size:0.75rem; font-weight:bold; color:${curSpec.walkable ? '#4ade80' : '#f87171'};">통과 가능 (walkable)</span>
          </label>

          <!-- Blocks Projectiles Toggle -->
          <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
            <input type="checkbox" id="gm-spec-blocks-proj" ${curSpec.blocksProjectiles ? 'checked' : ''} style="width:15px; height:15px;">
            <span style="font-size:0.75rem; color:#cbd5e1;">투사체 차단</span>
          </label>

          <!-- Speed Mult -->
          <div>
            <div style="font-size:0.72rem; color:#94a3b8;">이동속도 배율: <strong id="gm-lbl-speed">${curSpec.baseSpeedMult ?? 1.0}x</strong></div>
            <input type="range" id="gm-spec-speed" min="0" max="3.0" step="0.05" value="${curSpec.baseSpeedMult ?? 1.0}" style="width:100%;">
          </div>

          <!-- Evade Bonus -->
          <div>
            <div style="font-size:0.72rem; color:#94a3b8;">회피 보너스: <strong id="gm-lbl-evade">${curSpec.evadeBonusPct ?? 0}%</strong></div>
            <input type="range" id="gm-spec-evade" min="-50" max="50" step="5" value="${curSpec.evadeBonusPct ?? 0}" style="width:100%;">
          </div>

          <!-- Direct Damage (e.g. Lava) -->
          <div style="grid-column:span 2;">
            <div style="font-size:0.72rem; color:#94a3b8;">초당 직접 데미지 (지형 피해): <strong id="gm-lbl-dmg">${curSpec.directDamagePerSec ?? 0}/초</strong></div>
            <input type="range" id="gm-spec-dmg" min="0" max="50" step="1" value="${curSpec.directDamagePerSec ?? 0}" style="width:100%;">
          </div>
        </div>

        <button id="gm-btn-apply-tile-spec" style="width:100%; background:linear-gradient(135deg, #0284c7, #0369a1); border:none; color:#fff; font-weight:bold; padding:7px; border-radius:6px; cursor:pointer; font-size:0.78rem;">
          💾 [${curSpec.nameKr}] 속성값 즉시 전역 적용!
        </button>
      </div>
    `;
  }

  // ==========================================================================
  // TAB 3: 🌀 ZONE TELEPORT & WARP
  // ==========================================================================
  private renderTeleportTab(): string {
    const zones = [
      { id: 1, name: '☀️ 솔라리스 구릉지', note: '오크헤이븐 & 대가도' },
      { id: 8, name: '🏰 솔라리스 성 (도성)', note: '황금 대옥좌 & 왕궁' },
      { id: 9, name: '🏡 솔라리스 하우징 지구', note: '개인 및 길드 영지' },
      { id: 10, name: '💀 공동묘지 지하 1층', note: '고대 납골당 미로' },
      { id: 11, name: '💀 공동묘지 지하 2층', note: '심연의 망령 묘실' },
      { id: 12, name: '👑 공동묘지 B3 (대묘당)', note: '루나리스 3대 보스' },
      { id: 2, name: '🌾 그란델 평원', note: '윈드밀 타운 & 곡창지' },
      { id: 3, name: '🌲 실바나 대삼림', note: '엘프 고분 & 레이크우드' },
      { id: 4, name: '❄️ 보레아스 설원', note: '아이언프로스트 요새' },
      { id: 5, name: '🌫️ 네뷸라 늪지', note: '독안개 & 심연 히드라' },
      { id: 6, name: '🌋 이그니스 황무지', note: '화룡 이그나르 둥지' },
      { id: 7, name: '💎 루미나 성역섬', note: '크리스탈 신전 대제단' },
    ];

    return `
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="font-weight:bold; font-size:0.85rem; color:#fde047; margin-bottom:6px;">🌀 12개 전 지역 원클릭 순간이동</div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:12px;">
          ${zones.map((z) => `
            <button data-gm-warp-zone="${z.id}" style="
              background: #0f172a; border: 1px solid #334155; color: #fff; padding: 7px 8px; border-radius: 6px; text-align: left; cursor: pointer;
            ">
              <div style="font-weight:bold; font-size:0.75rem; color:#38bdf8;">${z.name}</div>
              <div style="font-size:0.65rem; color:#94a3b8;">${z.note}</div>
            </button>
          `).join('')}
        </div>

        <div style="border-top:1px solid #334155; padding-top:10px;">
          <div style="font-size:0.75rem; font-weight:bold; color:#cbd5e1; margin-bottom:6px;">📍 정밀 좌표 직접 워프:</div>
          <div style="display:flex; gap:6px;">
            <input type="number" id="gm-inp-coord-x" placeholder="X 좌표" value="80" style="flex:1; background:#0f172a; color:#fff; border:1px solid #475569; padding:5px; border-radius:4px; font-size:0.75rem;">
            <input type="number" id="gm-inp-coord-y" placeholder="Y 좌표" value="80" style="flex:1; background:#0f172a; color:#fff; border:1px solid #475569; padding:5px; border-radius:4px; font-size:0.75rem;">
            <button id="gm-btn-warp-coord" style="background:#f59e0b; border:none; color:#000; font-weight:bold; padding:5px 12px; border-radius:4px; cursor:pointer; font-size:0.75rem;">워프 이동</button>
          </div>
        </div>
      </div>
    `;
  }

  // ==========================================================================
  // TAB 4: 🎒 INVENTORY & TARGET INSPECTOR
  // ==========================================================================
  private renderItemsTab(player: EntityState | null, target: EntityState | null): string {
    const activeTarget = target || player;
    if (activeTarget) initEntityInventory20(activeTarget);
    const inv = activeTarget?.inventory || [];

    return `
      <!-- TARGET INFO -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div style="font-weight:bold; font-size:0.85rem; color:#fde047;">🎯 인벤토리 편집 대상: <span style="color:#38bdf8;">${activeTarget?.name || '없음'}</span></div>
          <button id="gm-btn-target-self" style="background:#334155; border:1px solid #475569; color:#cbd5e1; padding:3px 8px; border-radius:4px; font-size:0.7rem; cursor:pointer;">나 자신 선택</button>
        </div>

        <!-- 100-SLOT GRID PREVIEW -->
        <div style="font-size:0.72rem; color:#94a3b8; margin-bottom:6px;">100칸 인벤토리 현황 (클릭 시 1개 삭제 / 우클릭 시 수량 +10):</div>
        <div style="display:grid; grid-template-columns:repeat(5, 1fr); gap:5px; background:#0f172a; padding:8px; border-radius:6px; margin-bottom:10px; max-height:280px; overflow-y:auto;">
          ${inv.map((item, idx) => `
            <div data-gm-inv-slot="${idx}" style="
              background: ${item ? '#1e293b' : '#090d16'};
              border: 1px solid ${item ? '#f59e0b' : '#1e293b'};
              height: 48px; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center;
              cursor: pointer; position: relative;
            " title="${item ? `${item.name} (x${item.quantity})` : '빈 슬롯'}">
              <span style="font-size:1.1rem;">${item?.icon || ''}</span>
              ${item ? `<span style="font-size:0.6rem; color:#fde047; position:absolute; bottom:2px; right:3px;">x${item.quantity}</span>` : ''}
            </div>
          `).join('')}
        </div>

        <!-- INSTANT ITEM GRANT BUTTONS -->
        <div style="font-size:0.75rem; font-weight:bold; color:#cbd5e1; margin-bottom:6px;">🎁 재화 및 아이템 즉시 지급:</div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px;">
          <button data-gm-give-item="copper_1000" style="background:#7c2d12; border:1px solid #ea580c; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🪙 +1,000 코퍼 (1실버)</button>
          <button data-gm-give-item="gold_100" style="background:#854d0e; border:1px solid #eab308; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🪙 +100 골드 (물약 1병)</button>
          <button data-gm-give-item="plat_1" style="background:#4c1d95; border:1px solid #a855f7; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">💎 +1 플래티넘 (1,000G)</button>
          <button data-gm-give-item="potion_hp_20" style="background:#991b1b; border:1px solid #ef4444; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🧪 특급 체력 물약 (x20)</button>
          <button data-gm-give-item="potion_mp_20" style="background:#1e3a8a; border:1px solid #3b82f6; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🧪 특급 마나 물약 (x20)</button>
          <button data-gm-give-item="ticket_pack" style="background:#78350f; border:1px solid #f59e0b; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🐎 마구간 승마표 (100C)</button>
          <button data-gm-give-item="weapon_sword" style="background:#1e293b; border:1px solid #f59e0b; color:#fde047; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">⚔️ 왕실 전설 대검 (+65)</button>
          <button data-gm-give-item="clear_inv" style="background:#450a0a; border:1px solid #ef4444; color:#fca5a5; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; cursor:pointer;">🗑️ 인벤토리 비우기</button>
        </div>
      </div>
    `;
  }

  // ==========================================================================
  // TAB 5: 👾 MONSTER & NPC SPAWNER
  // ==========================================================================
  private renderSpawnerTab(player: EntityState | null): string {
    return `
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="font-weight:bold; font-size:0.85rem; color:#fde047; margin-bottom:8px;">👾 엔티티 실시간 소환 & 즉시 처치</div>

        <!-- BOSS SPAWN -->
        <div style="font-size:0.75rem; font-weight:bold; color:#ef4444; margin-bottom:6px;">👑 루나리스 3대 보스 & 월드 보스:</div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:12px;">
          <button data-gm-spawn-mob="skel_soldier" style="background:#450a0a; border:1px solid #ef4444; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            💀 해골화된 루나리스 병사
          </button>
          <button data-gm-spawn-mob="zombie_soldier" style="background:#14532d; border:1px solid #22c55e; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            🧟 좀비화된 루나리스 병사
          </button>
          <button data-gm-spawn-mob="vampire_bat" style="background:#3b0764; border:1px solid #a855f7; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            🦇 흡혈박쥐 (사역마)
          </button>
          <button data-gm-spawn-mob="dragon_ignar" style="background:#7c2d12; border:1px solid #ea580c; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            🐲 화룡 이그나르
          </button>
        </div>

        <!-- NPC SPAWN -->
        <div style="font-size:0.75rem; font-weight:bold; color:#38bdf8; margin-bottom:6px;">🧑‍🤝‍🧑 상인 & 아군 NPC:</div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:12px;">
          <button data-gm-spawn-mob="npc_jackson" style="background:#78350f; border:1px solid #f59e0b; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            🐎 마구간지기 잭슨
          </button>
          <button data-gm-spawn-mob="npc_king_solaris_3" style="background:#ca8a04; border:1px solid #eab308; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            👑 국왕 솔라리스 3세 소환
          </button>
          <button data-gm-spawn-mob="npc_guard"  style="background:#1e3a8a; border:1px solid #3b82f6; color:#fff; padding:6px; border-radius:4px; font-size:0.72rem; font-weight:bold; text-align:left; cursor:pointer;">
            🛡️ 솔라리스 왕실 근위병
          </button>
        </div>

        <!-- TARGET DISPOSITION & MOVE BUTTONS -->
        <div style="border-top:1px solid #334155; padding-top:10px; display:flex; flex-direction:column; gap:6px;">
          <div style="font-size:0.75rem; font-weight:bold; color:#cbd5e1;">📍 엔티티 및 NPC 재배치 / 처치 / 삭제:</div>
          <div style="display:flex; gap:6px;">
            <button id="gm-btn-toggle-move-spawner" style="
              flex: 1.2;
              background: ${this.moveActive ? '#f59e0b' : '#1e3a8a'};
              border: 1px solid ${this.moveActive ? '#fde047' : '#3b82f6'};
              color: ${this.moveActive ? '#000' : '#fff'};
              padding: 7px; border-radius: 4px; font-size: 0.75rem; font-weight: bold; cursor: pointer;
            ">
              ${this.moveActive ? (this.movingTarget ? `📍 [${this.movingTarget.name}] 착지 클릭!` : '📍 이동 모드 중 (대상 클릭)') : '📍 엔티티/NPC 이동 모드'}
            </button>
            <button id="gm-btn-kill-target" style="flex:1; background:#b91c1c; border:none; color:#fff; padding:7px; border-radius:4px; font-size:0.75rem; font-weight:bold; cursor:pointer;">
              ⚡ 타겟 즉시 처치
            </button>
            <button id="gm-btn-despawn-target" style="flex:1; background:#475569; border:none; color:#fff; padding:7px; border-radius:4px; font-size:0.75rem; font-weight:bold; cursor:pointer;">
              🗑️ 타겟 완전 삭제
            </button>
          </div>
          ${this.moveActive ? `
            <div style="font-size:0.7rem; color:#fde047; background:#0f172a; padding:6px 8px; border-radius:4px; border:1px dashed #f59e0b;">
              💡 <b>이동 방법:</b> 1단계: 이동시킬 NPC/몬스터/오브젝트를 클릭 ➔ 2단계: 원하는 목적지 바닥 타일을 클릭하면 즉시 재배치됩니다.
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  // ==========================================================================
  // TAB 6: 🔮 AI OBJECT FORGE & HOUSING (USER SCARE-CROW REQUEST!)
  // ==========================================================================
  private renderForgeTab(): string {
    const curPreset = this.forgePresets[this.selectedPresetIndex] || this.forgePresets[0];

    return `
      <!-- AI GENERATOR PRESETS & CUSTOM PROMPT -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px;">
        <div style="font-weight:bold; font-size:0.85rem; color:#fde047; margin-bottom:6px;">🔮 AI 오브젝트 제작소 (AI Object Forge)</div>
        <div style="font-size:0.72rem; color:#94a3b8; margin-bottom:10px;">
          테라 아니마 정통 16색 다크 판타지 화풍 100% 일치 레고 부품 조합 엔진으로 새로운 오브젝트를 생성합니다.
        </div>

        <!-- PRESET SELECTOR CARDS -->
        <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:6px; margin-bottom:10px;">
          ${this.forgePresets.map((p, idx) => `
            <div data-gm-forge-preset="${idx}" style="
              background: ${this.selectedPresetIndex === idx ? '#0f172a' : '#111827'};
              border: 1.5px solid ${this.selectedPresetIndex === idx ? '#f59e0b' : '#334155'};
              padding: 8px 6px; border-radius: 6px; text-align: center; cursor: pointer;
            ">
              <div style="font-size:1.5rem;">${p.icon}</div>
              <div style="font-size:0.72rem; font-weight:bold; color:${this.selectedPresetIndex === idx ? '#fde047' : '#fff'}; margin-top:2px;">${p.name}</div>
              <div style="font-size:0.62rem; color:#38bdf8;">${p.badge}</div>
            </div>
          `).join('')}
        </div>

        <!-- LIVE CANVAS PREVIEW OF SELECTED PRESET -->
        <div style="background:#0f172a; border:1px solid #334155; border-radius:6px; padding:10px; display:flex; gap:12px; align-items:center; margin-bottom:10px;">
          <canvas id="gm-forge-preview-canvas" width="80" height="80" style="background:#1e293b; border:1px solid #475569; border-radius:6px;"></canvas>
          <div style="flex:1;">
            <div style="font-weight:bold; font-size:0.82rem; color:#fde047;">${curPreset.icon} ${curPreset.name}</div>
            <div style="font-size:0.72rem; color:#cbd5e1; margin-top:3px; line-height:1.3;">${curPreset.desc}</div>
            <div style="font-size:0.68rem; color:#4ade80; margin-top:4px;">✨ 화풍 일치: 100% 테라 아니마 레트로 아이소메트릭 규격</div>
          </div>
        </div>

        <!-- CLAIM BUTTONS -->
        <div style="display:flex; gap:6px;">
          <button id="gm-btn-claim-forge-item" style="flex:1; background:linear-gradient(135deg, #f59e0b, #d97706); border:none; color:#000; font-weight:bold; padding:8px; border-radius:6px; cursor:pointer; font-size:0.78rem;">
            🎒 [${curPreset.name}] 인벤토리에 수령!
          </button>
          <button id="gm-btn-direct-place-forge" style="flex:1; background:linear-gradient(135deg, #10b981, #059669); border:none; color:#fff; font-weight:bold; padding:8px; border-radius:6px; cursor:pointer; font-size:0.78rem;">
            🔨 필드에 즉시 설치 모드
          </button>
        </div>
      </div>

      <!-- OBJECT TOOLS: MOVE & DEMOLITION -->
      <div style="background:#1e293b; border:1px solid #475569; border-radius:8px; padding:12px; display:flex; flex-direction:column; gap:10px;">
        <!-- Move Tool -->
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div>
            <div style="font-weight:bold; font-size:0.82rem; color:#38bdf8;">📍 오브젝트/NPC 이동기 (Move Relocator)</div>
            <div style="font-size:0.7rem; color:#94a3b8;">오브젝트나 NPC를 클릭하여 집어올린 후, 다른 타일을 클릭해 즉시 이동시킵니다.</div>
          </div>
          <button id="gm-btn-toggle-move-forge" style="
            background: ${this.moveActive ? '#f59e0b' : '#1e3a8a'};
            border: 1px solid ${this.moveActive ? '#fde047' : '#3b82f6'};
            color: ${this.moveActive ? '#000' : '#fff'};
            font-weight:bold; padding:6px 12px; border-radius:6px; cursor:pointer; font-size:0.75rem;
          ">${this.moveActive ? (this.movingTarget ? `📍 [${this.movingTarget.name}] 착지 클릭!` : '🔴 이동 대상 선택 중') : '📍 이동기 활성화'}</button>
        </div>

        <div style="border-top:1px dashed #334155;"></div>

        <!-- Demolition Tool -->
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div>
            <div style="font-weight:bold; font-size:0.82rem; color:#ef4444;">⛏️ 오브젝트 철거기 (Demolition Pickaxe)</div>
            <div style="font-size:0.7rem; color:#94a3b8;">활성화 후 필드의 오브젝트(허수아비, 벽 등)나 NPC를 클릭하면 영구 삭제합니다.</div>
          </div>
          <button id="gm-btn-toggle-demo" style="
            background: ${this.demolitionActive ? '#ef4444' : '#334155'};
            color: #fff; font-weight:bold; border:none; padding:6px 12px; border-radius:6px; cursor:pointer; font-size:0.75rem;
          ">${this.demolitionActive ? '🔴 철거 모드 중' : '⛏️ 철거기 활성화'}</button>
        </div>
      </div>
    `;
  }

  // ==========================================================================
  // 5. DOM EVENT BINDING
  // ==========================================================================
  private bindDOMEvents(player: EntityState | null, zone: any, target: EntityState | null): void {
    if (!this.containerEl) return;

    // Close button
    this.containerEl.querySelector('#gm-btn-close')?.addEventListener('click', () => this.close());

    // Tab buttons
    this.containerEl.querySelectorAll('[data-gm-tab]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).getAttribute('data-gm-tab') as any;
        if (tab) {
          this.activeTab = tab;
          this.render();
        }
      });
    });

    // --- TAB 1: CHEATS ---
    this.containerEl.querySelector('#gm-chk-godmode')?.addEventListener('change', (e) => {
      this.cheats.godMode = (e.target as HTMLInputElement).checked;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 무적 신 모드 (God Mode): ${this.cheats.godMode ? '활성화 (ON)' : '비활성화 (OFF)'}`,
        category: 'system',
      });
    });

    this.containerEl.querySelector('#gm-chk-zerocd')?.addEventListener('change', (e) => {
      this.cheats.zeroCooldown = (e.target as HTMLInputElement).checked;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 쿨타임 0초: ${this.cheats.zeroCooldown ? '활성화 (ON)' : '비활성화 (OFF)'}`,
        category: 'system',
      });
    });

    this.containerEl.querySelector('#gm-chk-instakill')?.addEventListener('change', (e) => {
      this.cheats.instaKill = (e.target as HTMLInputElement).checked;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 원펀치 즉사 모드: ${this.cheats.instaKill ? '활성화 (ON)' : '비활성화 (OFF)'}`,
        category: 'system',
      });
    });

    this.containerEl.querySelector('#gm-sel-speed')?.addEventListener('change', (e) => {
      this.cheats.superSpeedMult = parseFloat((e.target as HTMLSelectElement).value) || 1.0;
    });

    this.containerEl.querySelector('#gm-btn-fullheal')?.addEventListener('click', () => {
      for (const m of this.registry.getParty()) {
        m.resources.hp = m.resources.maxHp;
        m.resources.sp = m.resources.maxSp;
        m.resources.mp = m.resources.maxMp;
        m.isDead = false;
      }
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 파티 전원 즉시 부활 및 완전 치유 완료!`,
        category: 'heal',
      });
    });

    // Toggle King Lord British Avatar
    this.containerEl.querySelector('#gm-btn-toggle-king')?.addEventListener('click', () => {
      const p = this.registry.getPlayer();
      if (!p) return;
      if (!p.appearance) {
        p.appearance = {
          skinColor: '#fde68a',
          hairStyle: 'long',
          hairColor: '#d97706',
          facialFeature: 'beard',
          eyeColor: '#38bdf8',
          primaryArmorColor: '#fbbf24',
          secondaryArmorColor: '#d97706',
          capeColor: '#991b1b',
          showHelm: false,
          bodyScale: 1.08,
          titlePrefix: '솔라리스의 군주 (Lord British)',
          isKing: true,
        };
      } else {
        p.appearance.isKing = !p.appearance.isKing;
      }

      if (p.appearance.isKing) {
        p.avatarIcon = '👑';
        p.appearance.hairStyle = 'long';
        p.appearance.hairColor = '#d97706';
        p.appearance.facialFeature = 'beard';
        p.appearance.capeColor = '#991b1b';
        p.appearance.primaryArmorColor = '#fbbf24';
        p.appearance.secondaryArmorColor = '#d97706';
        p.appearance.showHelm = false;
        p.appearance.bodyScale = 1.08;
        p.horseColor = '#f8fafc';
        p.weaponType = 'pure';
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 관리자 캐릭터 외형이 '로드 브리티시 (황금 왕관 & 심홍색 담비 모피 망토 & 황금 홀)'로 전환되었습니다!`,
          category: 'system',
        });
      } else {
        p.avatarIcon = '🛡️';
        p.appearance.hairStyle = 'short';
        p.appearance.showHelm = true;
        p.appearance.bodyScale = 1.04;
        p.weaponType = 'slash';
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 관리자 외형이 기본 외형으로 복원되었습니다.`,
          category: 'system',
        });
      }
      this.render();
    });

    // Quick Job Switch (Supports switching directly or creating companion if not present)
    this.containerEl.querySelectorAll('[data-gm-switch-job]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const jobKey = (e.currentTarget as HTMLElement).getAttribute('data-gm-switch-job') || '';
        const player = this.registry.getPlayer();
        if (!player) return;

        const jobConfig: Record<
          string,
          { name: string; role: EntityRole; job: string; avatar: string; weaponType: any }
        > = {
          paladin: { name: '아서', role: 'tank', job: '성기사 (Paladin)', avatar: '🛡️', weaponType: 'slash' },
          wizard: { name: '엘드린', role: 'caster_dps', job: '비전 마법사 (Wizard)', avatar: '🔮', weaponType: 'crush' },
          archer: { name: '카엘렌', role: 'ranged_dps', job: '명사수 (Archer)', avatar: '🏹', weaponType: 'thrust' },
          cleric: { name: '라이라', role: 'healer', job: '치유 사제 (Cleric)', avatar: '✝️', weaponType: 'crush' },
          bard: { name: '로빈', role: 'buffer', job: '음유시인 (Bard)', avatar: '🎵', weaponType: 'thrust' },
          monk: { name: '턱', role: 'monk_hybrid', job: '수도승 (Monk)', avatar: '🥋', weaponType: 'crush' },
        };

        const conf = jobConfig[jobKey];
        if (!conf) return;

        // Directly transform current player's role, job and motion base
        player.role = conf.role;
        player.job = conf.job;
        player.weaponType = conf.weaponType;
        player.avatarIcon = conf.avatar;

        // Reset cooldowns and re-render
        player.cooldowns = {};
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 조작 캐릭터 직업이 '${conf.job}'(으)로 즉시 전환되었습니다!`,
          category: 'system',
        });
        this.render();
      });
    });

    // --- TAB 2: TERRAIN ---
    // Toggle Move Tool in Spawner & Forge Tabs
    const toggleMove = () => {
      this.moveActive = !this.moveActive;
      this.movingTarget = null;
      if (this.moveActive) {
        this.demolitionActive = false;
        this.placementActive = false;
        this.brushActive = false;
      }
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 오브젝트/NPC 이동 모드: ${this.moveActive ? '활성화 (필드의 대상을 클릭해 선택하세요)' : '비활성화'}`,
        category: 'system',
      });
      this.render();
    };

    this.containerEl.querySelector('#gm-btn-toggle-move-spawner')?.addEventListener('click', toggleMove);
    this.containerEl.querySelector('#gm-btn-toggle-move-forge')?.addEventListener('click', toggleMove);

    this.containerEl.querySelector('#gm-btn-toggle-brush')?.addEventListener('click', () => {
      this.brushActive = !this.brushActive;
      this.demolitionActive = false;
      this.placementActive = false;
      this.moveActive = false;
      this.movingTarget = null;
      this.render();
    });

    this.containerEl.querySelector('#gm-sel-brush-tile')?.addEventListener('change', (e) => {
      this.brushTile = (e.target as HTMLSelectElement).value as ZoneTileType;
    });

    this.containerEl.querySelectorAll('[data-brush-size]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        this.brushSize = parseInt((e.currentTarget as HTMLElement).getAttribute('data-brush-size') || '1') as any;
        this.render();
      });
    });

    this.containerEl.querySelector('#gm-btn-paint-underfoot')?.addEventListener('click', () => {
      if (player) {
        const rx = Math.round(player.x), ry = Math.round(player.y);
        if (zone.tiles[ry] && zone.tiles[ry][rx]) {
          zone.tiles[ry][rx] = this.brushTile;
          this.renderer.rebindGroundCache();
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: Date.now(),
            text: `[👑 GM] 발밑 타일 (${rx}, ${ry}) -> '${this.brushTile}' 변경 완료!`,
            category: 'system',
          });
        }
      }
    });

    this.containerEl.querySelector('#gm-sel-spec-tile')?.addEventListener('change', (e) => {
      this.selectedPropTile = (e.target as HTMLSelectElement).value as ZoneTileType;
      this.render();
    });

    this.containerEl.querySelector('#gm-btn-apply-tile-spec')?.addEventListener('click', () => {
      const walkable = (this.containerEl?.querySelector('#gm-spec-walkable') as HTMLInputElement)?.checked ?? true;
      const blocksProj = (this.containerEl?.querySelector('#gm-spec-blocks-proj') as HTMLInputElement)?.checked ?? false;
      const speed = parseFloat((this.containerEl?.querySelector('#gm-spec-speed') as HTMLInputElement)?.value || '1.0');
      const evade = parseInt((this.containerEl?.querySelector('#gm-spec-evade') as HTMLInputElement)?.value || '0', 10);
      const dmg = parseInt((this.containerEl?.querySelector('#gm-spec-dmg') as HTMLInputElement)?.value || '0', 10);

      updateTilePropertySpec(this.selectedPropTile, {
        walkable,
        blocksProjectiles: blocksProj,
        baseSpeedMult: speed,
        evadeBonusPct: evade,
        directDamagePerSec: dmg > 0 ? dmg : undefined,
      });

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 타일 '${this.selectedPropTile}' 속성값 전역 수정 완료! (통과: ${walkable}, 이속: ${speed}x, 회피: ${evade}%, 피해: ${dmg}/초)`,
        category: 'system',
      });
    });

    this.containerEl.querySelector('#gm-btn-reset-tile-specs')?.addEventListener('click', () => {
      resetTilePropertySpecs();
      this.render();
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `[👑 GM] 모든 타일 속성값이 공장 기본값으로 복원되었습니다.`,
        category: 'system',
      });
    });

    // Slider label live feedback
    this.containerEl.querySelector('#gm-spec-speed')?.addEventListener('input', (e) => {
      const val = (e.target as HTMLInputElement).value;
      const lbl = this.containerEl?.querySelector('#gm-lbl-speed');
      if (lbl) lbl.textContent = `${val}x`;
    });
    this.containerEl.querySelector('#gm-spec-evade')?.addEventListener('input', (e) => {
      const val = (e.target as HTMLInputElement).value;
      const lbl = this.containerEl?.querySelector('#gm-lbl-evade');
      if (lbl) lbl.textContent = `${val}%`;
    });
    this.containerEl.querySelector('#gm-spec-dmg')?.addEventListener('input', (e) => {
      const val = (e.target as HTMLInputElement).value;
      const lbl = this.containerEl?.querySelector('#gm-lbl-dmg');
      if (lbl) lbl.textContent = `${val}/초`;
    });

    // --- TAB 3: TELEPORT ---
    this.containerEl.querySelectorAll('[data-gm-warp-zone]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const zId = parseInt((e.currentTarget as HTMLElement).getAttribute('data-gm-warp-zone') || '1', 10);
        // Default safe landing coords based on zone dimensions
        let sx = 80, sy = 80;
        if (zId === 8) { sx = 64; sy = 80; } // Solaris Castle center
        else if (zId === 9) { sx = 80; sy = 80; } // Housing center
        else if (zId === 10) { sx = 32; sy = 16; } // Cemetery B1 (64x64)
        else if (zId === 11) { sx = 32; sy = 16; } // Cemetery B2 (64x64)
        else if (zId === 12) { sx = 64; sy = 64; } // Cemetery B3 (128x128)
        else if (zId === 7) { sx = 120; sy = 80; } // Lumina Sanctum
        this.teleportCallback(zId, sx, sy);
        this.render();
      });
    });

    this.containerEl.querySelector('#gm-btn-warp-coord')?.addEventListener('click', () => {
      const x = parseFloat((this.containerEl?.querySelector('#gm-inp-coord-x') as HTMLInputElement)?.value || '80');
      const y = parseFloat((this.containerEl?.querySelector('#gm-inp-coord-y') as HTMLInputElement)?.value || '80');
      if (player) {
        player.x = x;
        player.y = y;
        player.vx = 0;
        player.vy = 0;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 정밀 좌표 (${x}, ${y})로 순간이동 완료!`,
          category: 'system',
        });
        this.render();
      }
    });

    // --- TAB 4: ITEMS ---
    this.containerEl.querySelectorAll('[data-gm-give-item]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const itemType = (e.currentTarget as HTMLElement).getAttribute('data-gm-give-item');
        const activeTarget = target || player;
        if (!activeTarget) return;

        if (itemType === 'copper_1000') {
          activeTarget.gold = (activeTarget.gold || 0) + Currency.toCopper({ copper: 1000 });
        } else if (itemType === 'gold_100') {
          activeTarget.gold = (activeTarget.gold || 0) + Currency.toCopper({ gold: 100 });
        } else if (itemType === 'plat_1') {
          activeTarget.gold = (activeTarget.gold || 0) + Currency.toCopper({ platinum: 1 });
        } else if (itemType === 'potion_hp_20') {
          addItemToInventory(activeTarget, {
            id: 'pot_hp_grand',
            name: '특급 생명력 물약',
            category: 'consumable',
            rarity: 'fine',
            quantity: 20,
            maxStack: 99,
            weightKg: 0.1,
            valueGold: Currency.toCopper({ gold: 100 }), // 100골드
            icon: '🧪',
            description: '즉시 150 HP를 회복합니다. (개당 100골드)',
          });
        } else if (itemType === 'potion_mp_20') {
          addItemToInventory(activeTarget, {
            id: 'pot_mp_grand',
            name: '특급 마나 물약',
            category: 'consumable',
            rarity: 'fine',
            quantity: 20,
            maxStack: 99,
            weightKg: 0.1,
            valueGold: Currency.toCopper({ gold: 100 }), // 100골드
            icon: '🧪',
            description: '즉시 120 MP를 회복합니다. (개당 100골드)',
          });
        } else if (itemType === 'weapon_sword') {
          addItemToInventory(activeTarget, {
            id: 'wpn_gm_sun_blade',
            name: '솔라리스 왕실 대검',
            category: 'weapon',
            rarity: 'legendary',
            quantity: 1,
            maxStack: 1,
            weightKg: 4.5,
            valueGold: Currency.toCopper({ gold: 50 }),
            icon: '⚔️',
            description: '국왕의 하사품. 물리 공격력 +65, 힘 +15.',
          });
        } else if (itemType === 'ticket_pack') {
          addItemToInventory(activeTarget, {
            id: 'ticket_all_routes',
            name: '오크헤이븐행 승마표',
            category: 'quest',
            rarity: 'fine',
            quantity: 10,
            maxStack: 99,
            weightKg: 0.05,
            valueGold: 100, // 100 코퍼
            routeTargetStableId: 'stable_z1_oakhaven',
            icon: '🎫',
            description: '마구간지기에게 건네주면 오크헤이븐으로 이동하는 승마표 (100 코퍼)',
          });
        } else if (itemType === 'clear_inv') {
          if (activeTarget.inventory) {
            for (let i = 0; i < activeTarget.inventory.length; i++) {
              activeTarget.inventory[i] = null;
            }
          }
        }
        this.render();
      });
    });

    // Target Self Button
    this.containerEl.querySelector('#gm-btn-target-self')?.addEventListener('click', () => {
      const p = this.registry.getPlayer();
      if (p) {
        p.targetId = null;
        this.render();
      }
    });

    // 100-Slot Grid Preview: Click to remove 1, Right-click to add 10
    this.containerEl.querySelectorAll('[data-gm-inv-slot]').forEach((slotEl) => {
      const slotIdx = parseInt((slotEl as HTMLElement).getAttribute('data-gm-inv-slot') || '-1', 10);
      if (slotIdx < 0) return;

      const activeTarget = target || player;
      if (!activeTarget || !activeTarget.inventory) return;

      slotEl.addEventListener('click', (e) => {
        e.preventDefault();
        const it = activeTarget.inventory![slotIdx];
        if (it) {
          if (it.quantity > 1) {
            it.quantity -= 1;
          } else {
            activeTarget.inventory![slotIdx] = null;
          }
          this.render();
        }
      });

      slotEl.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        const it = activeTarget.inventory![slotIdx];
        if (it) {
          it.quantity = Math.min(it.maxStack || 99, it.quantity + 10);
          this.render();
        }
      });
    });

    // --- TAB 5: SPAWNER ---
    this.containerEl.querySelectorAll('[data-gm-spawn-mob]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const mobKey = (e.currentTarget as HTMLElement).getAttribute('data-gm-spawn-mob');
        if (!player) return;
        const sx = player.x + 2, sy = player.y + 2;
        this.spawnEntityByKey(mobKey || '', sx, sy);
      });
    });

    this.containerEl.querySelector('#gm-btn-kill-target')?.addEventListener('click', () => {
      if (target) {
        target.resources.hp = 0;
        target.isDead = true;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 타겟 '${target.name}' 즉시 처치 완료!`,
          category: 'combat',
        });
        this.render();
      }
    });

    this.containerEl.querySelector('#gm-btn-despawn-target')?.addEventListener('click', () => {
      if (target && target.id !== player?.id) {
        this.registry.unregister(target.id);
        if (player) player.targetId = null;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 타겟 '${target.name}' 완전 삭제(Despawn) 완료!`,
          category: 'system',
        });
        this.render();
      }
    });

    // --- TAB 6: AI FORGE ---
    this.containerEl.querySelectorAll('[data-gm-forge-preset]').forEach((card) => {
      card.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).getAttribute('data-gm-forge-preset') || '0', 10);
        this.selectedPresetIndex = idx;
        this.render();
        this.drawForgePreviewCanvas();
      });
    });

    this.containerEl.querySelector('#gm-btn-claim-forge-item')?.addEventListener('click', () => {
      const curPreset = this.forgePresets[this.selectedPresetIndex];
      if (player && curPreset) {
        initEntityInventory20(player);
        addItemToInventory(player, {
          id: curPreset.id,
          name: curPreset.name,
          category: 'quest',
          rarity: 'masterwork',
          quantity: 1,
          maxStack: 20,
          weightKg: 2.0,
          valueGold: 100,
          icon: curPreset.icon,
          description: curPreset.desc,
        });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] '${curPreset.name}' 아이템을 관리자 인벤토리에 지급했습니다! (우클릭하여 필드 배치 가능)`,
          category: 'system',
        });
        this.render();
      }
    });

    this.containerEl.querySelector('#gm-btn-direct-place-forge')?.addEventListener('click', () => {
      const curPreset = this.forgePresets[this.selectedPresetIndex];
      if (curPreset) {
        this.placementActive = true;
        this.demolitionActive = false;
        this.brushActive = false;
        this.moveActive = false;
        this.movingTarget = null;
        this.pendingProp = {
          type: curPreset.type,
          name: curPreset.name,
          customDraw: curPreset.draw,
        };
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: `[👑 GM] 필드 설치 모드 활성화! 필드의 원하는 타일을 클릭하세요. (${curPreset.name})`,
          category: 'system',
        });
        this.render();
      }
    });

    this.containerEl.querySelector('#gm-btn-toggle-demo')?.addEventListener('click', () => {
      this.demolitionActive = !this.demolitionActive;
      this.placementActive = false;
      this.brushActive = false;
      this.moveActive = false;
      this.movingTarget = null;
      this.render();
    });

    // Draw preview canvas if on forge tab
    if (this.activeTab === 'forge') {
      setTimeout(() => this.drawForgePreviewCanvas(), 10);
    }
  }

  // ==========================================================================
  // 6. AI FORGE MODULAR PRESETS (100% STYLE CONSISTENT!)
  // ==========================================================================
  private initDefaultForgePresets(): void {
    this.forgePresets = [
      {
        id: 'prop_scarecrow_straw',
        type: 'scarecrow_straw',
        name: '볏짚 훈련용 허수아비',
        icon: '🌾',
        desc: '밀짚모자를 쓴 볏짚 인형. 타격 시 데미지(DPS)를 측정할 수 있는 훈련용 더미.',
        badge: '훈련용 더미',
        draw: (ctx, sx, sy) => {
          ctx.fillStyle = '#451a03';
          ctx.fillRect(sx - 2.5, sy - 38, 5, 38);
          ctx.fillRect(sx - 15, sy - 27, 30, 4);
          ctx.fillStyle = '#ca8a04';
          ctx.fillRect(sx - 6, sy - 26, 12, 16);
          ctx.fillStyle = '#713f12';
          ctx.fillRect(sx - 5, sy - 20, 10, 2);
          ctx.fillStyle = '#1e3a8a';
          ctx.fillRect(sx - 7, sy - 25, 14, 11);
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.arc(sx, sy - 31, 5.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#eab308';
          ctx.beginPath();
          ctx.ellipse(sx, sy - 34, 11, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ca8a04';
          ctx.fillRect(sx - 4.5, sy - 42, 9, 8);
          ctx.font = '10px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('🎯', sx, sy - 46);
        },
      },
      {
        id: 'prop_scarecrow_pumpkin',
        type: 'scarecrow_pumpkin',
        name: '호박머리 괴물 허수아비',
        icon: '🎃',
        desc: '불타는 눈빛의 잭오랜턴 호박 허수아비. 어두운 밤에 까마귀와 들짐승을 쫓아냅니다.',
        badge: '야간 발광',
        draw: (ctx, sx, sy) => {
          ctx.fillStyle = '#451a03';
          ctx.fillRect(sx - 2.5, sy - 38, 5, 38);
          ctx.fillRect(sx - 14, sy - 26, 28, 4);
          ctx.fillStyle = '#1e1b4b';
          ctx.fillRect(sx - 7, sy - 26, 14, 18);
          ctx.fillStyle = '#ea580c';
          ctx.beginPath();
          ctx.arc(sx, sy - 33, 8, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(sx - 3, sy - 35, 2, 2);
          ctx.fillRect(sx + 1, sy - 35, 2, 2);
          ctx.fillRect(sx - 2, sy - 31, 4, 2);
        },
      },
      {
        id: 'prop_training_dummy',
        type: 'training_dummy',
        name: '기사 훈련용 목각 인형',
        icon: '🛡️',
        desc: '강철 투구와 가죽 흉갑을 덧댄 정예 기사용 훈련 표적. 높은 내구도를 지닙니다.',
        badge: '기사 훈련용',
        draw: (ctx, sx, sy) => {
          ctx.fillStyle = '#78350f';
          ctx.fillRect(sx - 3, sy - 36, 6, 36);
          ctx.fillStyle = '#b45309';
          ctx.fillRect(sx - 10, sy - 28, 20, 18);
          ctx.strokeStyle = '#dc2626';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(sx, sy - 19, 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = '#94a3b8';
          ctx.fillRect(sx - 6, sy - 36, 12, 9);
          ctx.font = '10px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('⚔️', sx, sy - 42);
        },
      },
      {
        id: 'prop_lamp_post',
        type: 'lamp_post',
        name: '중세 석조 호박 가로등',
        icon: '🏮',
        desc: '야간에 따뜻한 호박색 불빛을 밝혀 주변 시야를 밝혀주는 고딕 스타일 가로등.',
        badge: '야간 조명',
        draw: (ctx, sx, sy) => {
          ctx.fillStyle = '#334155';
          ctx.fillRect(sx - 2, sy - 42, 4, 42);
          ctx.fillStyle = '#f59e0b';
          ctx.fillRect(sx - 5, sy - 46, 10, 8);
          ctx.fillStyle = 'rgba(245, 158, 11, 0.2)';
          ctx.beginPath();
          ctx.arc(sx, sy - 42, 16, 0, Math.PI * 2);
          ctx.fill();
        },
      },
      {
        id: 'prop_bench_stone',
        type: 'bench_stone',
        name: '왕립 정원 석조 벤치',
        icon: '🪑',
        desc: '모험가들이 앉아 쉴 수 있는 고급 대리석 조각 벤치.',
        badge: '휴식 조경',
        draw: (ctx, sx, sy) => {
          ctx.fillStyle = '#475569';
          ctx.fillRect(sx - 14, sy - 8, 4, 8);
          ctx.fillRect(sx + 10, sy - 8, 4, 8);
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(sx - 16, sy - 12, 32, 4);
        },
      },
    ];
  }

  private drawForgePreviewCanvas(): void {
    const canvas = document.getElementById('gm-forge-preview-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw small isometric tile base
    const cx = canvas.width / 2;
    const cy = canvas.height - 18;
    ctx.fillStyle = '#22c55e';
    ctx.strokeStyle = '#15803d';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 8);
    ctx.lineTo(cx + 24, cy);
    ctx.lineTo(cx, cy + 8);
    ctx.lineTo(cx - 24, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw preset on top
    const cur = this.forgePresets[this.selectedPresetIndex];
    if (cur && cur.draw) {
      cur.draw(ctx, cx, cy, 0);
    }
  }

  // ==========================================================================
  // 7. ENTITY SPAWN HELPER
  // ==========================================================================
  private spawnEntityByKey(key: string, x: number, y: number): void {
    const id = `gm_spawn_${key}_${Date.now()}`;

    if (key === 'skel_soldier') {
      this.registry.register({
        id,
        name: '💀 [BOSS] 해골화된 루나리스 병사',
        category: 'monster',
        role: 'monster',
        job: '루나리스 최하급 척후병',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 3.4,
        resources: { hp: 1350, maxHp: 1350, sp: 400, maxSp: 400, mp: 300, maxMp: 300, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#dc2626',
        weaponType: 'slash',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '💀',
        appearance: { skinColor: '#f8fafc', hairStyle: 'short', hairColor: '#000', facialFeature: 'none', eyeColor: '#ef4444', primaryArmorColor: '#1e293b', secondaryArmorColor: '#0f172a', capeColor: '#991b1b', showHelm: true, bodyScale: 3.0 },
      });
    } else if (key === 'zombie_soldier') {
      this.registry.register({
        id,
        name: '🧟 [BOSS] 좀비화된 루나리스 병사',
        category: 'monster',
        role: 'monster',
        job: '루나리스 최하급 보병',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 2.6,
        resources: { hp: 1500, maxHp: 1500, sp: 500, maxSp: 500, mp: 200, maxMp: 200, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#16a34a',
        weaponType: 'crush',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🧟',
        appearance: { skinColor: '#22c55e', hairStyle: 'long', hairColor: '#0f172a', facialFeature: 'none', eyeColor: '#facc15', primaryArmorColor: '#14532d', secondaryArmorColor: '#052e16', capeColor: '#166534', showHelm: false, bodyScale: 3.0 },
      });
    } else if (key === 'vampire_bat') {
      this.registry.register({
        id,
        name: '🦇 [BOSS] 흡혈박쥐',
        category: 'monster',
        role: 'monster',
        job: '루나리스 정찰 사역마',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'N',
        speed: 4.8,
        resources: { hp: 1200, maxHp: 1200, sp: 350, maxSp: 350, mp: 500, maxMp: 500, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#9333ea',
        weaponType: 'thrust',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🦇',
        appearance: { skinColor: '#3b0764', hairStyle: 'short', hairColor: '#1e1b4b', facialFeature: 'none', eyeColor: '#f43f5e', primaryArmorColor: '#581c87', secondaryArmorColor: '#2e1065', capeColor: '#701a75', showHelm: false, bodyScale: 3.0 },
      });
    } else if (key === 'dragon_ignar') {
      this.registry.register({
        id,
        name: '🐲 고대 화염룡 이그나르',
        category: 'monster',
        role: 'monster',
        job: '타락한 왕실 수호룡',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 3.8,
        resources: { hp: 1200, maxHp: 1200, sp: 400, maxSp: 400, mp: 400, maxMp: 400, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#ea580c',
        weaponType: 'slash',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🐲',
        appearance: { skinColor: '#991b1b', hairStyle: 'long', hairColor: '#451a03', facialFeature: 'none', eyeColor: '#fbbf24', primaryArmorColor: '#7c2d12', secondaryArmorColor: '#451a03', capeColor: '#ea580c', showHelm: true, bodyScale: 3.2 },
      });
    } else if (key === 'npc_jackson') {
      this.registry.register({
        id,
        name: '마구간지기 잭슨',
        category: 'npc',
        role: 'buffer',
        job: '마구간지기',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 2.0,
        resources: { hp: 500, maxHp: 500, sp: 100, maxSp: 100, mp: 100, maxMp: 100, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#f59e0b',
        weaponType: 'crush',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🐎',
        appearance: { skinColor: '#fed7aa', hairStyle: 'short', hairColor: '#78350f', facialFeature: 'none', eyeColor: '#451a03', primaryArmorColor: '#d97706', secondaryArmorColor: '#78350f', capeColor: '#000', showHelm: false, bodyScale: 1.0 },
      });
    } else if (key === 'npc_guard') {
      this.registry.register({
        id,
        name: '솔라리스 왕실 근위병',
        category: 'npc',
        role: 'tank',
        job: '왕실 근위기사',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 3.2,
        resources: { hp: 800, maxHp: 800, sp: 300, maxSp: 300, mp: 100, maxMp: 100, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#3b82f6',
        weaponType: 'slash',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🛡️',
        appearance: { skinColor: '#e2e8f0', hairStyle: 'short', hairColor: '#1e293b', facialFeature: 'none', eyeColor: '#38bdf8', primaryArmorColor: '#1e3a8a', secondaryArmorColor: '#1e293b', capeColor: '#dc2626', showHelm: true, bodyScale: 1.0 },
      });
    } else if (key === 'npc_king_solaris_3') {
      this.registry.register({
        id,
        name: '👑 국왕 솔라리스 3세',
        category: 'npc',
        role: 'npc',
        job: '솔라리스 왕국 국왕',
        x,
        y,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 3.0,
        resources: { hp: 9999, maxHp: 9999, sp: 1000, maxSp: 1000, mp: 2000, maxMp: 2000, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#eab308',
        weaponType: 'slash',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '👑',
        appearance: { skinColor: '#fed7aa', hairStyle: 'long', hairColor: '#e2e8f0', facialFeature: 'beard', eyeColor: '#38bdf8', primaryArmorColor: '#ca8a04', secondaryArmorColor: '#854d0e', capeColor: '#dc2626', showHelm: true, bodyScale: 1.2 },
      });
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: `[👑 GM] (${Math.round(x)}, ${Math.round(y)}) 좌표에 '${key}' 소환 완료!`,
      category: 'system',
    });
  }
}
